import { inject, ObjectGraph } from '@jet/environment/dependencies';
import { ObjectGraphType } from '../../dependencies/object-graph-type';

// Types
import type {
    ClassicalAPIResponse,
    ClassicalListViewPage,
} from '../../api/types';
import type { SearchPage } from '../../models/pages/search';

// Utils
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import {
    createPageMetricsForResponse,
    PageType,
} from '@jet-app/music-classical/metrics';
import type { Section } from '@jet-app/music-classical/models/section/Section';
import { makeCompoundAction } from '@jet-app/music-classical/action-model';
import { StorageKeys } from '@jet-app/music-classical/utilities/storage/StorageKeys';
import {
    renderRecentlySearchedSection,
    recentSearchItemsFromPersistenceValue,
} from './renderRecentlySearchedSection';
import { addingSearchSeoData } from '@jet-app/music-classical/utilities/seo/Search';
import { renderEmptyPage } from '@jet-app/music-classical/utilities/transforms/empty-view';

export function renderSearchPage(
    canonicalURL: string,
    apiResponse: ClassicalAPIResponse,
    term: string,
    objectGraph: ObjectGraph,
): SearchPage {
    let pageTitle = null; // No Page Title required for Landing or Search Results, but only display if No Results
    const responseBody = apiResponse?.body as ClassicalListViewPage;
    const apiSections = responseBody?.sections as any;

    let sections = transformSections(
        apiSections,
        objectGraph,
        responseBody?.screenType,
    );

    sections = updateRecentlySearchedSegue(sections, objectGraph);

    if (!term) {
        // Add Recently Searched Section to the Search Page for landing page (no term present)
        const recentlySearchedSection =
            renderRecentlySearchedSection(objectGraph);
        if (recentlySearchedSection) {
            sections.unshift(recentlySearchedSection);
        }
    }

    // Empty Results Section & Updates
    const localization = inject(ObjectGraphType.localization, objectGraph);
    if (sections.length === 0) {
        // Overriding `firstPage` object as it is not returned by server
        // but is expected in `renderEmptyPage`
        responseBody.firstPage = {
            icon: 'search',
            type: 'empty',
            description:
                localization.string(
                    'ASE.Web.MusicClassicalClientJS.Search.NoResults.TryAgain',
                ) || '',
            heading: localization.string(
                'ASE.Web.MusicClassicalClientJS.Search.NoResults.PageHeader',
            ),
        };
        return renderEmptyPage<SearchPage>(
            canonicalURL,
            apiResponse,
            objectGraph,
        );
    }

    const page = {
        pageKind: 'componentScreen',
        kind: 'SearchPage',
        screenId: responseBody?.screenId,
        screenType: responseBody?.screenType,
        header: {
            title: pageTitle,
        },
        sections,
        canonicalURL,
        pageMetrics: createPageMetricsForResponse(
            apiResponse,
            PageType.searchPage,
        ),
        term,
    } as SearchPage;

    return addingSearchSeoData(canonicalURL, page, objectGraph);
}

function updateRecentlySearchedSegue(
    sections: Section<unknown>[],
    objectGraph: ObjectGraph,
): Section<unknown>[] {
    return sections?.length > 0
        ? sections.map((section) => {
              // Skip if its the Link Lockup
              if (section.itemKind === 'linkLockup') {
                  return section;
              }

              section.items = section.items.map((item) =>
                  updateItemSegueWithCompoundAction(item, objectGraph),
              );
              return section;
          })
        : [];
}

function updateItemSegueWithCompoundAction(
    item: any,
    objectGraph: ObjectGraph,
) {
    // Add
    const catalogSearchItem: any = {
        timestamp: new Date(),
        meta: {
            artwork: item.artwork,
            title: item.title,
            subtitle: item.subtitle,
            headline: item.headline,
            type: item.type,
            url: item.url,
            action: item.action,
            contextMenuAction: item.contextMenuAction,
            playAction: item.playAction,
        },
    };

    const storage = inject(ObjectGraphType.storage, objectGraph);
    const recentSearchesPersistentValue = storage.getItem(
        StorageKeys.User.RecentlySearchedItems,
    );
    const recentCatalogSearches =
        recentSearchItemsFromPersistenceValue(recentSearchesPersistentValue) ??
        [];
    const setPersistentSearchesAction = storage.setItemAction(
        StorageKeys.User.RecentlySearchedItems,
        [...recentCatalogSearches, catalogSearchItem],
    );

    if (item.segue) {
        item.segue = makeCompoundAction({ custom: {}, data: [] }, [
            setPersistentSearchesAction,
            item.segue,
        ]);
    }

    return item;
}
